/**
 * MSAAResolvePass - Resolves all multisampled render targets to single-sampled textures.
 *
 * When MSAA is enabled, all rendering happens to multisampled textures. This pass
 * resolves them to single-sampled textures that can be read by post-processing passes.
 *
 * Two sub-passes:
 * 1. Auto-resolve for color and overlay (hardware average-resolve via resolveTarget)
 * 2. Custom resolve for normals, viewDepth, and depth (sample 0 via fragment shader)
 */

import { DepthFormat, DepthClearValue } from '../CommonRenderTargets';
import { USE_WBOIT } from '../../globals';
import msaaResolveShader from './msaa_resolve.wgsl';

/** @import { Renderer } from '../Renderer' */

export class MSAAResolvePass {
  /** @type {Renderer} */
  #renderer;
  /** @type {GPUDevice} */
  #device;

  // Auto-resolve pass (color + overlay)
  /** @type {GPURenderPassDescriptor|null} */
  #autoResolveDescriptor = null;

  // Custom resolve pass (normals + viewDepth + depth)
  /** @type {GPURenderPipeline|null} */
  #customResolvePipeline = null;
  /** @type {GPUBindGroupLayout|null} */
  #customResolveBindGroupLayout = null;
  /** @type {GPUBindGroup|null} */
  #customResolveBindGroup = null;
  /** @type {GPURenderPassDescriptor|null} */
  #customResolveDescriptor = null;

  /**
   * @param {Renderer} renderer
   */
  constructor(renderer) {
    this.#renderer = renderer;
  }

  init() {
    this.#device = this.#renderer.getDevice();

    this.#customResolveBindGroupLayout = this.#device.createBindGroupLayout({
      label: 'msaa-custom-resolve-bind-group-layout',
      entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.FRAGMENT,
        texture: { sampleType: 'unfilterable-float', multisampled: true }
      },
      {
        binding: 1,
        visibility: GPUShaderStage.FRAGMENT,
        texture: { sampleType: 'unfilterable-float', multisampled: true }
      },
      {
        binding: 2,
        visibility: GPUShaderStage.FRAGMENT,
        texture: { sampleType: 'depth', multisampled: true }
      }]

    });

    const shaderModule = this.#device.createShaderModule({
      label: 'msaa-custom-resolve-shader',
      code: msaaResolveShader
    });

    this.#customResolvePipeline = this.#device.createRenderPipeline({
      label: 'msaa-custom-resolve-pipeline',
      layout: this.#device.createPipelineLayout({
        bindGroupLayouts: [this.#customResolveBindGroupLayout]
      }),
      vertex: {
        module: shaderModule,
        entryPoint: 'vsmain'
      },
      fragment: {
        module: shaderModule,
        entryPoint: 'psmain',
        targets: [
        { format: 'rgb10a2unorm' }, // normals
        { format: 'rgb10a2unorm' } // viewDepth
        ]
      },
      primitive: {
        topology: 'triangle-list',
        cullMode: 'none'
      },
      depthStencil: {
        format: DepthFormat,
        depthWriteEnabled: true,
        depthCompare: 'always'
      }
    });
  }

  /**
   * Updates resolve descriptors when render targets are resized.
   */
  resize() {
    const rt = this.#renderer.getRenderTargets();
    const sampleCount = rt.getSampleCount();

    if (sampleCount <= 1) {
      this.#autoResolveDescriptor = null;
      this.#customResolveBindGroup = null;
      this.#customResolveDescriptor = null;
      return;
    }

    // Sub-pass A: Auto-resolve for color, overlay, and WBOIT targets
    const autoResolveAttachments = [
    {
      view: rt.getColorTarget().createView(),
      resolveTarget: rt.getColorTargetForSampling().createView(),
      loadOp: 'load',
      storeOp: 'store'
    },
    {
      view: rt.getOverlayTarget().createView(),
      resolveTarget: rt.getOverlayTargetForSampling().createView(),
      loadOp: 'load',
      storeOp: 'store'
    }];


    if (USE_WBOIT) {
      autoResolveAttachments.push(
        {
          view: rt.getWboitAccumView(),
          resolveTarget: rt.getWboitAccumViewForSampling(),
          loadOp: 'load',
          storeOp: 'store'
        },
        {
          view: rt.getWboitRevealageView(),
          resolveTarget: rt.getWboitRevealageViewForSampling(),
          loadOp: 'load',
          storeOp: 'store'
        }
      );
    }

    this.#autoResolveDescriptor = {
      label: 'msaa-auto-resolve-pass',
      colorAttachments: autoResolveAttachments
    };

    // Sub-pass B: Custom resolve for normals + viewDepth + depth
    this.#customResolveBindGroup = this.#device.createBindGroup({
      label: 'msaa-custom-resolve-bind-group',
      layout: this.#customResolveBindGroupLayout,
      entries: [
      { binding: 0, resource: rt.getNormalsTarget().createView() },
      { binding: 1, resource: rt.getViewDepthTarget().createView() },
      { binding: 2, resource: rt.getDepthTarget().createView() }]

    });

    this.#customResolveDescriptor = {
      label: 'msaa-custom-resolve-pass',
      colorAttachments: [
      {
        view: rt.getNormalsTargetForSampling().createView(),
        loadOp: 'clear',
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        storeOp: 'store'
      },
      {
        view: rt.getViewDepthTargetForSampling().createView(),
        loadOp: 'clear',
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        storeOp: 'store'
      }],

      depthStencilAttachment: {
        view: rt.getDepthTargetForSampling().createView(),
        depthClearValue: DepthClearValue,
        depthLoadOp: 'clear',
        depthStoreOp: 'store'
      }
    };
  }

  /**
   * Encodes the MSAA resolve passes into the given command encoder. The caller is
   * responsible for submitting it.
   * @param {GPUCommandEncoder} commandEncoder
   */
  run(commandEncoder) {
    if (!this.#autoResolveDescriptor) {
      return;
    }

    // Sub-pass A: Auto-resolve color + overlay
    const autoPass = commandEncoder.beginRenderPass(this.#autoResolveDescriptor);
    autoPass.end();

    // Sub-pass B: Custom resolve normals + viewDepth + depth
    const customPass = commandEncoder.beginRenderPass(this.#customResolveDescriptor);
    customPass.setPipeline(this.#customResolvePipeline);
    customPass.setBindGroup(0, this.#customResolveBindGroup);
    customPass.draw(3);
    customPass.end();
  }
}